import type { External } from '@quromedical/models';
import {
  Alert,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

import { ExternalCard } from './card';
import { ExternalForm } from './form';

interface PractitionerIntegrationsFormProps {
  canEdit: boolean;
  data?: Partial<External>;
  revalidate: () => void;
  onSubmit: (practitioner: External) => Promise<void>;
}

type FormState = 'initial' | 'editing' | 'submitting' | 'error';

export const PractitionerExternalCard: React.FC<
  PractitionerIntegrationsFormProps
> = ({ canEdit, data, revalidate, onSubmit }) => {
  const state = useFiniteState<FormState>('initial');

  const handleSubmit = useCallback(
    async (result: External) => {
      state.set('submitting');
      try {
        await onSubmit(result);
        state.set('initial');
        revalidate();
        return {};
      } catch (error) {
        state.set('error');
        logger.error(error);
        return { error };
      }
    },
    [revalidate, onSubmit, state],
  );

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <ExternalCard
              data={data}
              canEdit={canEdit}
              onEditPress={state.next('editing')}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <ExternalForm
              data={data}
              handleCancel={state.next('initial')}
              handleSubmit={handleSubmit}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
